import "./App.css";
import Login from "./views/auth/Login";
import Layout from "./views/layout/Layout";
import Settings from "./views/layout/Settings";
import Dashboard from "./views/mainDashboard/Dashboard";
import ProtectedRoute from "../src/utils/auth/ProtectedRoute";
import { GlobalProvider } from "./context/GlobalContext";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
  Title,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import SuperAdminRoute from "./utils/auth/SuperAdminRoute";
import ClientsList from "./views/admin/clients/ClientsList";
import CreateClient from "./views/admin/clients/CreateClient";
import AdminDashboard from "./views/admin/dashboard/AdminDashboard";
import { xAxisLabelTooltipPlugin } from "./utils/chartOptions/dashboard/adminDashboard";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import SurveysListOld from "./views/XMOTSurveys/surveys/SurveyListOld";
import annotationPlugin from "chartjs-plugin-annotation";
import SetNewPassword from "./views/auth/SetNewPassword";
import CreateSurveyFrame from "./views/XMOTSurveys/surveys/CreateSurveyFrame";
import UsersList from "./views/admin/manageUsers/UsersList";
import CreateUser from "./views/admin/manageUsers/CreateUser";
import InviteUserResetPassword from "./views/auth/InviteUserResetPassword";
import ClientRoute from "./utils/auth/ClientRoute";
import ViewResults from "./views/XMOTSurveys/surveys/ViewResults";
import Configuration from "./views/admin/configurationSettings/Configuration";
import XmotTaskQuestion from "./views/xmotTaskQuestion/XmotTaskQuestion";
import SurveyList from "./views/XMOTSurveys/surveys/SurveyList";
import CreateSurvey from "./views/XMOTSurveys/surveys/CreateSurvey/CreateSurvey";
import { Toast } from "primereact/toast";
import { setToastRef } from "./services/toast/toastService";
import { PrimeReactProvider } from "primereact/api";
import ViewResultsData from "./views/XMOTSurveys/ViewResults/ViewResultData";
import OtherActivities from "./views/admin/OtherActivities/OtherActivities";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import { setClarityUser } from "./utils/clarity/clarity";

// Global PrimeReact config
const primeReactConfig = {
  hideOverlaysOnDocumentScrolling: true, // default false
  // appendTo: 'self',                         // where overlays attach
  // ripple: true,                             // ripple effects
};

// Register Chart.js plugins
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
  Title,
  ChartDataLabels,
  Tooltip,
  xAxisLabelTooltipPlugin,
  annotationPlugin,
);

// Routes config
const router = createBrowserRouter(
  [
    {
      path: "/login",
      element: (
        <ProtectedRoute isLoginRoute={true}>
          <Login />
        </ProtectedRoute>
      ),
    },
    {
      path: "/reset-password",
      element: (
        <ProtectedRoute isLoginRoute={true}>
          <SetNewPassword />
        </ProtectedRoute>
      ),
    },
    {
      path: "/user-reset-password",
      element: (
        <ProtectedRoute isLoginRoute={true}>
          <InviteUserResetPassword />
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin-panel/*",
      element: (
        <SuperAdminRoute>
          <Layout />
        </SuperAdminRoute>
      ),
      children: [
        { index: true, element: <AdminDashboard /> },
        { path: "clients", element: <ClientsList /> },
        { path: "clients/create", element: <CreateClient /> },
        { path: "clients/edit/:id", element: <CreateClient /> },
        { path: "categories/:id", element: <Dashboard /> },
        { path: "survey-old/:id", element: <SurveysListOld /> },
        { path: "survey/:id", element: <SurveyList /> },
        { path: "survey/create-old/:id", element: <CreateSurveyFrame /> },
        { path: "survey/create/:id", element: <CreateSurvey /> },
        { path: "survey/edit-old/:id", element: <CreateSurveyFrame /> },
        { path: "survey/edit/:id", element: <CreateSurvey /> },
        { path: "survey/clone/:id", element: <CreateSurveyFrame /> },
        { path: "users/:id", element: <UsersList /> },
        { path: "users/create/:id", element: <CreateUser /> },
        { path: "users/edit/:id", element: <CreateUser /> },
        { path: "survey/view-results/:id", element: <ViewResults /> },
        { path: "configuration-settings", element: <Configuration /> },
        { path: "xmot-task-questions", element: <XmotTaskQuestion /> },
        { path: "survey/view-results/:id", element: <ViewResults /> },
        { path: "other-activities/:id", element: <OtherActivities /> },
        {
          path: "other-activities/view-results/:id",
          element: <ViewResults isCategoryViewResults={true} />,
        },
        // { path: "survey/new-view-results/:id", element: <ViewResultsData /> },
      ],
    },
    {
      path: "/dashboard/*",
      element: (
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      ),
      children: [
        { index: true, element: <Dashboard /> },
        { path: "survey-old", element: <SurveysListOld /> },
        { path: "survey", element: <SurveyList /> },
        { path: "survey/create-old", element: <CreateSurveyFrame /> },
        { path: "survey/create", element: <CreateSurvey /> },
        { path: "survey/edit-old", element: <CreateSurveyFrame /> },
        { path: "survey/edit", element: <CreateSurvey /> },
        { path: "survey/clone", element: <CreateSurveyFrame /> },
        { path: "survey/view-results", element: <ViewResults /> },
        { path: "other-activities", element: <OtherActivities /> },
        {
          path: "other-activities/view-results",
          element: <ViewResults isCategoryViewResults={true} />,
        },
        {
          path: "users",
          element: (
            <ClientRoute>
              <UsersList />
            </ClientRoute>
          ),
        },
        {
          path: "users/create",
          element: (
            <ClientRoute>
              <CreateUser />
            </ClientRoute>
          ),
        },
        {
          path: "users/edit/:id",
          element: (
            <ClientRoute>
              <CreateUser />
            </ClientRoute>
          ),
        },
      ],
    },
    { path: "/", element: <Navigate to="/login" replace /> },
    { path: "*", element: <Navigate to="/login" replace /> },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);

function App() {
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (user) {
      const adminDetail = {
        id: user?.id,
        loginClientName: `${user?.firstname}`,
        email: user?.email,
      };
      setClarityUser(adminDetail);
    }
  }, [user]);

  return (
    // Added due to fixes of hideOverlaysOnDocumentScrolling issue after closing inspect with open PrimeReact dropdowns/modals.
    <PrimeReactProvider value={primeReactConfig}>
      <div className="App">
        <Toast ref={(ref) => setToastRef(ref)} />
        <GlobalProvider>
          <RouterProvider router={router} />
        </GlobalProvider>
      </div>
    </PrimeReactProvider>
  );
}

export default App;
